import React, { useCallback } from 'react';

import { FormattedMessage } from 'react-intl';

import RovoIcon from '@atlaskit/icon-lab/core/rovo';
import AiIcon from '@atlaskit/icon/core/atlassian-intelligence';
import { fg } from '@atlaskit/platform-feature-flags';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';

import { useAnalyticsEvents } from '../../../../../common/analytics/generated/use-analytics-events';
import { messages } from '../../../../../messages';
import type { AISummaryActionData } from '../../../../../state/flexible-ui-context/types';
import type {
	AISummaryState,
	AISummaryStatus,
} from '../../../../../state/hooks/use-ai-summary/ai-summary-service/types';
import { useBlockCardRovoActionExperimentNoExposure } from '../../../../../state/hooks/use-block-card-rovo-action-experiment';
import Action from '../action';

import type { AISummaryActionProps } from './types';
import { getErrorMessage } from './utils';

export function AISummariseAction({
	onClick: onClickCallback,
	onError: onErrorCallback,
	testId,
	status,
	summariseUrl,
	...props
}: AISummaryActionProps &
	AISummaryActionData & {
		status: AISummaryStatus;
		summariseUrl: () => Promise<AISummaryState> | undefined;
	}): React.JSX.Element {
	const { fireEvent } = useAnalyticsEvents();
	const isRovoBlockCardExperimentEnabled = useBlockCardRovoActionExperimentNoExposure();

	const onCompleted = useCallback(
		(state: AISummaryState) => {
			if (state?.status === 'error') {
				onErrorCallback?.(getErrorMessage(state.error));
			}
		},
		[onErrorCallback],
	);

	const handleActionClick = useCallback(() => {
		fireEvent('ui.button.clicked.aiSummary', {});

		fireEvent('track.aiInteraction.initiated', {
			aiFeatureName: 'Smart Links Summary',
			proactiveAIGenerated: 0,
			userGeneratedAI: 1,
		});

		summariseUrl()?.then(onCompleted, onCompleted);

		onClickCallback?.();
	}, [fireEvent, onClickCallback, onCompleted, summariseUrl]);

	const Icon = fg('platform_sl_ai_summary_rebrand') ? RovoIcon : AiIcon;

	return (
		<Action
			content={
				<FormattedMessage
					{...(fg('platform_sl_ai_summary_rebrand')
						? messages.ai_summary_action_rebrand
						: messages.ai_summary_action)}
				/>
			}
			icon={
				<Icon
					spacing="spacious"
					color="currentColor"
					label="Summarise with AI"
					{...(fg('platform_sl_3p_auth_rovo_action_kill_switch') ||
					isRovoBlockCardExperimentEnabled ||
					expValEqualsNoExposure('rovogrowth-640-inline-action-nudge-exp', 'isEnabled', true)
						? { size: props.iconSize }
						: {})}
				/>
			}
			onClick={handleActionClick}
			testId={`${testId}-summarise-action`}
			isLoading={status === 'loading'}
			tooltipMessage={
				<FormattedMessage
					{...(fg('platform_sl_ai_summary_rebrand')
						? messages.ai_summary_action_description_rebrand
						: messages.ai_summary_action_description)}
				/>
			}
			{...props}
		/>
	);
}
